import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Sparkles, Send, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { useChatbot } from "../lib/chatbot";

const WELCOME = {
  role: "assistant",
  text: "Hi — I'm your Welljio companion. Ask me anything: products, orders, shipping, returns, warranty, payments, or even posture & sleep tips. How can I help today?",
};

const QUICK_PROMPTS = [
  { label: "Recommend a product", text: "I have neck pain at night and sit at a desk all day — which Welljio product is right for me?" },
  { label: "Shipping & delivery", text: "How long does shipping take and what's the delivery cost?" },
  { label: "Returns & refunds", text: "What's your returns policy? How do I return an item?" },
  { label: "Warranty", text: "What warranty do Welljio products come with?" },
  { label: "Payment options", text: "What payment methods do you accept? Is Cash on Delivery available?" },
  { label: "Order tracking", text: "How do I track my order and see its current status?" },
  { label: "Posture tips", text: "Can you share quick posture tips for a long workday at a desk?" },
];

export default function AIChatbot() {
  const { sessionId } = useCart();
  const { pathname } = useLocation();
  const { open, setOpen } = useChatbot();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [messages, open]);

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: trimmed }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/ai/chat`, { session_id: `chat-${sessionId}`, message: trimmed });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "I had trouble responding just now. Could you try again in a moment?" },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const resetConversation = () => {
    setMessages([WELCOME]);
    setInput("");
  };


  // Render assistant text with simple **bold** + auto-linkified product paths
  const renderRichText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\/(?:product|symptom-checker|account|login|shop|blog|contact|faq)(?:\/[\w-]+)?)/g);
    return parts.map((part, i) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) {
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      if (/^\/(?:product|symptom-checker|account|login|shop|blog|contact|faq)(?:\/[\w-]+)?$/.test(part)) {
        return (
          <a key={i} href={part} className="text-welljio-accent underline underline-offset-2 hover:text-welljio-ink">
            {part}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <>
      <button
        data-testid="ai-chatbot-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[10000] w-14 h-14 rounded-full bg-welljio-ink text-white shadow-[0_18px_40px_-12px_rgba(2,132,199,0.55)] hover:scale-105 hover:bg-welljio-accent transition-all flex items-center justify-center"
        aria-label="Open chat"
      >
        {open ? <X className="w-5 h-5" strokeWidth={1.8} /> : <MessageCircle className="w-5 h-5" strokeWidth={1.8} />}
      </button>

      {open && (
        <div
          data-testid="ai-chatbot-panel"
          className="fixed bottom-24 right-6 z-[10000] w-[92vw] max-w-md h-[70vh] max-h-[640px] welljio-glass-card rounded-3xl flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-gradient-to-b from-welljio-highlight/40 to-white">
            <div className="w-10 h-10 rounded-2xl bg-welljio-ink text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-welljio-ink">Welljio Companion</p>
              <p className="text-xs text-welljio-muted">Ask anything — products, orders, posture</p>
            </div>
            {messages.length > 1 && (
              <button
                data-testid="chat-reset-button"
                onClick={resetConversation}
                aria-label="Start new conversation"
                className="w-8 h-8 rounded-full hover:bg-welljio-tint flex items-center justify-center text-welljio-muted"
              >
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6} />
              </button>
            )}
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-${m.role}-message`}
                  className={`max-w-[88%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-welljio-ink text-white rounded-br-md"
                      : "bg-welljio-tint text-welljio-ink rounded-bl-md"
                  }`}
                >
                  {m.role === "assistant" ? renderRichText(m.text) : m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-welljio-tint text-welljio-muted px-4 py-3 rounded-2xl text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-welljio-muted rounded-full animate-pulse-soft" />
                  <span className="w-2 h-2 bg-welljio-muted rounded-full animate-pulse-soft" style={{ animationDelay: "0.2s" }} />
                  <span className="w-2 h-2 bg-welljio-muted rounded-full animate-pulse-soft" style={{ animationDelay: "0.4s" }} />
                  <span className="text-xs ml-1">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-5 pb-3" data-testid="chat-quick-prompts">
              <p className="text-[10px] uppercase tracking-[0.22em] text-welljio-subtle mb-2">Quick starts</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q.label}
                    data-testid={`chat-quick-prompt-${q.label.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => sendMessage(q.text)}
                    className="text-xs px-3 py-1.5 rounded-full bg-welljio-highlight/60 text-welljio-accent hover:bg-welljio-highlight transition-colors"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 border-t border-slate-100 flex gap-2">
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Ask anything — neck pain, returns, posture tips…"
              className="flex-1 bg-welljio-tint rounded-full px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-welljio-teal/40"
            />
            <button
              data-testid="chat-send-button"
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              className="w-11 h-11 rounded-full bg-welljio-ink text-white flex items-center justify-center disabled:opacity-40 hover:bg-welljio-accent transition-colors"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
